import Phaser from 'phaser';
import {
  answerFriendRequest,
  dismissInvite,
  hubInvites,
  hubRequests,
  joinInvite,
  onHub,
  refreshInbox,
} from '../hub';
import { whoText } from '../nicknames';
import { blockPlayer, onlineOn } from '../online';
import { MODE_WORDS } from '../modeWords';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { drawHeader, drawTabBar, TABBAR_H } from '../ui/navChrome';
import { label, UI } from '../ui/text';
import { wipeTo } from './WipeScene';

const SIZE_NAME = { quick: 'QUICK', normal: 'NORMAL', long: 'LONG' } as const;

/**
 * CLUB's INBOX (the envelope in the header; docs/FRIENDS.md): friend requests (ACCEPT, NOT NOW,
 * which declines quietly, or BLOCK) and friends' invites still open (JOIN, or X to let one go),
 * newest first. Updates as they come and go.
 */
export class InboxScene extends Phaser.Scene {
  private unsubscribe: (() => void) | null = null;
  /** A line under the header: what just happened. */
  private note: string | null = null;

  constructor() {
    super('Inbox');
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.note = null;
    this.unsubscribe = onHub(this.redraw);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.unsubscribe?.());
    // Invites run out on their own: look again now and then.
    this.time.addEvent({ delay: 5000, loop: true, callback: this.redraw });
    this.draw();
    void refreshInbox();
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    drawHeader(this, 'INBOX', 'club');
    drawTabBar(this, 'club');
    const backY = height - TABBAR_H - 26;
    bevelButton(this, 6, backY, width - 12, 20, 'BACK', () => wipeTo(this, 'Trophy'));
    const g = this.add.graphics();
    let y = 30;
    if (!onlineOn()) {
      label(this, width / 2, y + 4, 'Online play is off.', 1, UI.dim).setOrigin(0.5, 0);
      return;
    }
    if (this.note) {
      label(this, width / 2, y, this.note, 1, UI.good).setOrigin(0.5, 0);
      y += 12;
    }
    const requests = hubRequests();
    const invites = hubInvites();
    if (requests.length === 0 && invites.length === 0) {
      label(this, width / 2, y + 10, 'Invites and friend requests show up here.', 1, UI.dim)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
      return;
    }

    // Friend requests: a name, then three answers.
    if (requests.length > 0) {
      label(this, 6, y, `FRIEND REQUESTS (${requests.length})`, 1, UI.dim);
      y += 12;
      const w = Math.floor((width - 28) / 3);
      for (const r of requests) {
        const h = 36;
        if (y + h > backY - 4) break;
        navyPanel(g, 4, y, width - 8, h);
        label(this, 10, y + 5, whoText(r.player, r.name), 1, UI.accent);
        bevelButton(
          this,
          10,
          y + 17,
          w,
          14,
          'ACCEPT',
          () => void this.answer(r.player, 'accept'),
          true,
        );
        bevelButton(
          this,
          14 + w,
          y + 17,
          w,
          14,
          'NOT NOW',
          () => void this.answer(r.player, 'decline'),
        );
        bevelButton(
          this,
          18 + 2 * w,
          y + 17,
          w,
          14,
          'BLOCK',
          () => void this.answer(r.player, 'block'),
        );
        y += h + 2;
      }
      y += 4;
    }

    // Invites: who, what, JOIN or X.
    if (invites.length > 0 && y + 40 < backY) {
      label(this, 6, y, `INVITES (${invites.length})`, 1, UI.dim);
      y += 12;
      for (const invite of invites) {
        const h = 28;
        if (y + h > backY - 4) break;
        navyPanel(g, 4, y, width - 8, h);
        const what = MODE_WORDS[invite.mode].name;
        label(this, 10, y + 5, whoText(invite.from.player, invite.from.name), 1, UI.accent);
        label(this, 10, y + 16, `${SIZE_NAME[invite.size]} ${what}`, 1, UI.dim);
        bevelButton(this, width - 74, y + 6, 44, 16, 'JOIN', () => joinInvite(this, invite), true);
        bevelButton(this, width - 26, y + 6, 18, 16, 'X', () => dismissInvite(invite.code));
        y += h + 2;
      }
    }
  }

  /** Accepts, declines (quietly: the sender is never told) or blocks a friend request's sender. */
  private async answer(sender: string, how: 'accept' | 'decline' | 'block'): Promise<void> {
    const name = hubRequests().find((r) => r.player === sender);
    const who = name ? whoText(name.player, name.name) : 'them';
    const done =
      how === 'block'
        ? (await blockPlayer(sender)) && (await answerFriendRequest(sender, false))
        : await answerFriendRequest(sender, how === 'accept');
    this.note = !done
      ? "Can't reach the server right now."
      : how === 'accept'
        ? `Friends with ${who}!`
        : how === 'block'
          ? `Blocked ${who}.`
          : null;
    if (how === 'block') void refreshInbox();
    this.redraw();
  }
}
